import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useCurrentUser } from './useCurrentUser';
import { apiService } from '../services/api';
import { 
  TurnState, 
  TurnPermissions, 
  Player, 
  defaultTurnState,
  TURN_TIME_PRESETS 
} from '../types/turn';

interface UseTurnControlOptions {
  tableId: string;
  players: Player[];
  masterId?: string;
  websocket?: WebSocket | null; // Receber websocket do GameSession (envio)
  subscribe: (fn: (data: any) => void) => () => void; // Hub: escutar mensagens já parseadas
  isConnected?: boolean;
}

interface UseTurnControlReturn {
  // Estado do turno
  turnState: TurnState;
  
  // Permissões derivadas
  permissions: TurnPermissions;
  
  // Informações úteis
  currentPlayer: Player | null;
  nextPlayer: Player | null;
  isLastPlayer: boolean;
  
  // Funções de controle (apenas para mestre)
  startTurn: (playerOrder: string[], totalTime?: number, iniciarAgora?: boolean) => Promise<void>;
  startCountdown: () => Promise<void>;  // Iniciar contagem (despausar)
  pauseTurn: () => Promise<void>;       // Pausar contagem
  goToNextPlayer: () => Promise<void>;
  adjustTime: (seconds: number) => Promise<void>;
  endTurn: () => Promise<void>;
  
  // Utilitários
  formatTime: (seconds: number) => string;
  getTimeColor: (timeRemaining: number, totalTime: number) => string;
  refreshTurnState: () => void;
  loadTurnStateHTTP: () => Promise<void>;
}

export function useTurnControl({ tableId, players, masterId, websocket, subscribe, isConnected = false }: UseTurnControlOptions): UseTurnControlReturn {
  const { user } = useCurrentUser();
  
  const [turnState, setTurnState] = useState<TurnState>(defaultTurnState);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estado para rastrear ajustes manuais de tempo (para sincronização)
  const [lastServerUpdate, setLastServerUpdate] = useState<{
    timeRemaining: number;
    timestamp: number;
  } | null>(null);
  
  // Função para enviar mensagens via websocket
  const sendMessage = useCallback(async (data: any): Promise<void> => {
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      const error = new Error(`WebSocket não conectado. State: ${websocket?.readyState || 'null'}`)
      console.error('[TURN] WebSocket error:', error)
      throw error;
    }
    
    return new Promise((resolve, reject) => {
      try {
        websocket.send(JSON.stringify(data));
        resolve();
      } catch (error) {
        console.error('[TURN] Error sending message:', error)
        reject(error);
      }
    });
  }, [websocket]);
  
  // Memoizar permissões baseadas no estado atual
  const permissions = useMemo((): TurnPermissions => {
    const isMaster = Boolean(user && masterId && user.id === masterId);
    const isMyTurn = Boolean(user && turnState.isActive && user.id === turnState.currentPlayerId);
    const canSendMessage = !turnState.isActive || isMaster || isMyTurn;
    const canControlTurn = isMaster;
    
    return {
      canSendMessage,
      canControlTurn,
      isMyTurn,
      isMaster,
    };
    // Depender só dos campos usados: turnState muda 10x/s pelo timer local
    // (timeRemaining) e invalidava o TurnContext inteiro a cada tick.
  }, [user, masterId, turnState.isActive, turnState.currentPlayerId]);
  
  // Encontrar players atuais e próximos
  const currentPlayer = useMemo(() => {
    if (!turnState.currentPlayerId) return null;
    return players.find(p => p.id === turnState.currentPlayerId) || null;
  }, [turnState.currentPlayerId, players]);
  
  const nextPlayer = useMemo(() => {
    if (!turnState.isActive || !turnState.playerOrder.length) return null;
    
    const currentIndex = turnState.playerOrder.indexOf(turnState.currentPlayerId || '');
    if (currentIndex === -1) return null;
    
    const nextIndex = (currentIndex + 1) % turnState.playerOrder.length;
    const nextPlayerId = turnState.playerOrder[nextIndex];
    
    return players.find(p => p.id === nextPlayerId) || null;
  }, [turnState, players]);
  
  const isLastPlayer = useMemo(() => {
    if (!turnState.isActive || !turnState.playerOrder.length) return false;
    
    const currentIndex = turnState.playerOrder.indexOf(turnState.currentPlayerId || '');
    return currentIndex === turnState.playerOrder.length - 1;
  }, [turnState]);

  // Solicitar estado atual
  const refreshTurnState = useCallback(() => {
    if (!isConnected || !websocket) return;
    
    const message = {
      type: 'get_turn_state',
      table_id: tableId,
    };
    
    sendMessage(message).catch(error => {
      console.error('[TURN] Erro ao solicitar estado do turno:', error);
    });
  }, [isConnected, sendMessage, tableId, websocket]);

  // Carregar estado inicial via HTTP (fallback)
  const loadTurnStateHTTP = useCallback(async () => {
    try {
      const turnData = await apiService.getTurnState(tableId);
      
      if (turnData) {
        // Registrar último update do servidor para sincronização
        setLastServerUpdate({
          timeRemaining: turnData.time_remaining ?? 0,
          timestamp: Date.now()
        });
        
        setTurnState(prevState => ({
          isActive: turnData.is_active ?? false,
          isPaused: turnData.is_paused ?? false,
          currentPlayerId: turnData.current_player_id ?? null,
          playerOrder: turnData.player_order ?? [],
          timeRemaining: turnData.time_remaining ?? 0,
          totalTime: turnData.total_time ?? 0,
          masterId: turnData.master_id ?? prevState.masterId ?? '',
          turnNumber: turnData.turn_number ?? prevState.turnNumber ?? 0,
          startedAt: turnData.started_at ? new Date(turnData.started_at) : null,
        }));
      }
    } catch (error) {
      console.error('[TURN] Erro ao carregar estado via HTTP:', error);
    }
  }, [tableId]);
  
  // Listener para atualizações de estado do turno (via hub — parse único)
  useEffect(() => {
    const handleTurnStateUpdate = (data: any) => {
      if (data.type === 'turn_state_updated' && data.data) {
        // Registrar último update do servidor para sincronização
        setLastServerUpdate({
          timeRemaining: data.data.time_remaining ?? 0,
          timestamp: Date.now()
        });
        
        setTurnState(prevState => {
          const newState: TurnState = {
            isActive: data.data.is_active ?? false,
            isPaused: data.data.is_paused ?? false,
            currentPlayerId: data.data.current_player_id ?? null,
            playerOrder: data.data.player_order ?? [],
            timeRemaining: data.data.time_remaining ?? 0,
            totalTime: data.data.total_time ?? 0,
            masterId: data.data.master_id ?? prevState.masterId ?? '',
            turnNumber: data.data.turn_number ?? prevState.turnNumber ?? 0,
            startedAt: data.data.started_at ? new Date(data.data.started_at) : null,
          };
          return newState;
        });
      }
    };
    
    // Recusa do servidor precisa chegar aos olhos do mestre. Desde que a
    // permissao passou a ser conferida no banco (fase 1), um comando pode ser
    // recusado com o botao visivel — sem este aviso, o mestre so ve um botao
    // que nao faz nada no meio da aula.
    const handleError = (data: any) => {
      if (data.type !== 'error') return;
      if (data.code && !String(data.code).startsWith('turno_')) return;

      console.error('[TURN] Erro do servidor:', data.message);
      toast.error(data.message || 'O servidor recusou o comando de turno.');
    };
    
    // Inscrever no hub (recebe o data já parseado)
    const handleData = (data: any) => {
      handleTurnStateUpdate(data);
      handleError(data);
    };
    const unsubscribe = subscribe(handleData);

    // Solicitar estado atual quando conectar
    if (isConnected) {
      refreshTurnState();
      // Também carregar via HTTP como fallback
      loadTurnStateHTTP();
    }

    return unsubscribe;
  }, [subscribe, isConnected, tableId, refreshTurnState, loadTurnStateHTTP]);

  // Timer local para contagem regressiva - SINCRONIZADO com ajustes do servidor
  useEffect(() => {
    if (!turnState.isActive || turnState.isPaused || !lastServerUpdate) {
      return;
    }

    const intervalId = setInterval(() => {
      setTurnState(prevState => {
        if (!prevState.isActive || prevState.isPaused || !lastServerUpdate) {
          return prevState;
        }
        
        // Calcular tempo baseado no último update do servidor
        const now = Date.now();
        const elapsedSinceUpdate = Math.floor((now - lastServerUpdate.timestamp) / 1000);
        const calculatedTimeRemaining = Math.max(0, lastServerUpdate.timeRemaining - elapsedSinceUpdate);
        
        
        return {
          ...prevState,
          timeRemaining: calculatedTimeRemaining,
        };
      });
    }, 100); // Atualizar a cada 100ms para maior precisão

    return () => {
      clearInterval(intervalId);
    };
  }, [turnState.isActive, turnState.isPaused, lastServerUpdate?.timestamp]); // Depende de quando recebeu último update e se está pausado
  
  // Funções de controle (apenas mestre pode executar)
  const startTurn = useCallback(async (
    playerOrder: string[],
    totalTime = TURN_TIME_PRESETS.medium,
    iniciarAgora = false,
  ) => {
    if (!permissions.canControlTurn || isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const message = {
        type: 'turn_start',
        table_id: tableId,
        player_order: playerOrder,
        total_time: totalTime,
        // A rodada abre PARADA: o mestre monta a fila, narra a cena e so
        // entao entrega o tempo ao jogador, no botao "Iniciar" da barra de
        // cima. O servidor aceita os dois modos; quem escolhe e esta linha.
        iniciar_agora: iniciarAgora,
      };

      await sendMessage(message);
    } catch (error) {
      console.error('[TURN] Erro ao iniciar turno:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [permissions.canControlTurn, isLoading, sendMessage, tableId]);
  

  const nextPlayerAction = useCallback(async () => {
    if (!permissions.canControlTurn || isLoading) return;
    
    setIsLoading(true);
    try {
      await sendMessage({
        type: 'turn_next',
        table_id: tableId,
      });
    } catch (error) {
      console.error('[TURN] Erro ao avançar turno:', error);
    } finally {
      setIsLoading(false);
    }
  }, [permissions.canControlTurn, isLoading, sendMessage, tableId]);
  
  const adjustTime = useCallback(async (seconds: number) => {
    if (!permissions.canControlTurn || isLoading) return;
    
    setIsLoading(true);
    try {
      await sendMessage({
        type: 'turn_adjust_time',
        table_id: tableId,
        time_adjustment: seconds,
      });
    } catch (error) {
      console.error('[TURN] Erro ao ajustar tempo:', error);
    } finally {
      setIsLoading(false);
    }
  }, [permissions.canControlTurn, isLoading, sendMessage, tableId]);
  
  const startCountdown = useCallback(async () => {
    if (!permissions.canControlTurn || isLoading) return;
    
    setIsLoading(true);
    try {
      await sendMessage({
        type: 'turn_start_countdown',
        table_id: tableId,
      });
    } catch (error) {
      console.error('[TURN] Erro ao iniciar contagem:', error);
    } finally {
      setIsLoading(false);
    }
  }, [permissions.canControlTurn, isLoading, sendMessage, tableId]);
  
  const pauseTurn = useCallback(async () => {
    if (!permissions.canControlTurn || isLoading) return;
    
    setIsLoading(true);
    try {
      await sendMessage({
        type: 'turn_pause',
        table_id: tableId,
      });
    } catch (error) {
      console.error('[TURN] Erro ao pausar turno:', error);
    } finally {
      setIsLoading(false);
    }
  }, [permissions.canControlTurn, isLoading, sendMessage, tableId]);
  
  const endTurn = useCallback(async () => {
    if (!permissions.canControlTurn || isLoading) return;
    
    setIsLoading(true);
    try {
      await sendMessage({
        type: 'turn_end',
        table_id: tableId,
      });
    } catch (error) {
      console.error('[TURN] Erro ao finalizar turno:', error);
    } finally {
      setIsLoading(false);
    }
  }, [permissions.canControlTurn, isLoading, sendMessage, tableId]);
  
  // Utilitários de formatação
  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);
  
  const getTimeColor = useCallback((timeRemaining: number, totalTime: number): string => {
    const ratio = timeRemaining / totalTime;
    
    if (ratio > 0.6) return 'text-green-500'; // Verde
    if (ratio > 0.3) return 'text-yellow-500'; // Amarelo
    return 'text-red-500'; // Vermelho
  }, []);
  
  return {
    turnState,
    permissions,
    currentPlayer,
    nextPlayer,
    isLastPlayer,
    
    // Funções de controle
    startTurn,
    startCountdown,
    pauseTurn,
    goToNextPlayer: nextPlayerAction,
    adjustTime,
    endTurn,
    
    // Utilitários
    formatTime,
    getTimeColor,
    refreshTurnState,
    loadTurnStateHTTP,
  };
}